var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { validateCompletion } from '../../../../../backend/config/classifiers/AccountSectionClassifier/shared';
import { accountEntityName, getAccountPermissions, mapAccountSectionToAuditTag, } from '../../../../../backend/features/accounts/shared';
import { groupAccountMapEntityName } from '../../../../../backend/features/groups/shared';
import { filterAccountOrganizationsByPermission } from '../../../../../backend/features/organizations/shared';
import { adminOrSelf, allowAccessToGroups, allowOrgMembersEdit, requireAnySystemPermission } from '../../../../../backend/features/permissions/shared';
import { useAccountAuditBackendDataProvider } from '../../../../../ui/features/accounts/hooks/useAccountAuditBackendDataProvider';
import { useUserState } from '../../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../../ui/hooks/useContactManagementTranslation';
import { ContactManagementConfigContext } from '../../../../../ui/providers';
import { collectFilesAndFilterData } from '@juvare/common-form-ui/WidgetFormFile/utils';
import { ColorPaper } from '@juvare/common-web-ui/ColorPaper';
import { handleErrorDialog, showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { handleErrorSnackbar, showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { baseClientDefaults } from '@juvare/platform-sdk-js/BaseClient';
import { EntityAudit } from '@juvare/platform-sdk-react/ui/features/audit';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useAttachmentClient, usePlatformLoader, useStorageClient, useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Box, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React from 'react';
import { useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useAccountSections } from '../../hooks/useAccountSections';
import { AccountSectionComponent } from '../AccountSectionComponent/AccountSectionComponent';
import { AccountProfileHeader } from './components/AccountProfileHeader';
export const AccountPage = ({ customAccountProfileHeaderComponent, accountEntityAuditFormatters }) => {
    const navigationContext = React.useContext(NavigationContext);
    const { accountConfig } = React.useContext(ContactManagementConfigContext);
    const { onStatelessReload } = usePlatformLoader({});
    const settings = useSelector(getSettings);
    const userState = useUserState();
    const userClient = useUserClient();
    const accountStorage = useStorageClient(accountEntityName);
    const location = useLocation();
    const { t } = useContactManagementTranslation();
    const accountAttachmentStorage = useAttachmentClient();
    const { accountId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const [account, setAccount] = React.useState();
    const [accountPermissions, setAccountPermissions] = React.useState();
    const [reload, setReload] = React.useState({});
    const [searchParams] = useSearchParams();
    const allowViewAccountHistory = requireAnySystemPermission(userState, 'view-account-history');
    React.useEffect(() => {
        if (accountId) {
            accountStorage.get(accountId).then((response) => {
                if (userState.id !== response.id && !requireAnySystemPermission(userState, 'manage-accounts')) {
                    const { orgRole, orgStatus } = filterAccountOrganizationsByPermission(response.data, response.id, ['manage-members'], userState);
                    response.data.orgRole = orgRole;
                    response.data.orgStatus = orgStatus;
                }
                setAccount(response);
                setAccountPermissions(getAccountPermissions(settings, userState, response.id, response.data));
            });
        }
    }, [accountId, settings, accountStorage, userState, reload]);
    const sections = allowedSections(userState, account, useAccountSections('details').data);
    let activeTab = sections === null || sections === void 0 ? void 0 : sections.findIndex((section) => urlify(section.columns.title) === searchParams.get('tab'));
    if (!activeTab || activeTab === -1) {
        if (searchParams.get('tab') === 'history' && allowViewAccountHistory) {
            activeTab = ((sections === null || sections === void 0 ? void 0 : sections.length) || 0) + 1;
        }
        else {
            activeTab = 0;
        }
    }
    if (!account || accountPermissions === undefined)
        return null;
    const validationResult = validateCompletion(sections !== null && sections !== void 0 ? sections : [], accountConfig, account.data);
    const AccountProfileHeaderComponent = customAccountProfileHeaderComponent !== null && customAccountProfileHeaderComponent !== void 0 ? customAccountProfileHeaderComponent : AccountProfileHeader;
    return (React.createElement(Box, { sx: { height: '100%', overflow: 'scroll' } },
        React.createElement(ColorPaper, { gradient: 'purple-dark-linear' },
            React.createElement(AccountProfileHeaderComponent, { account: account, accountPermissions: accountPermissions })),
        sections && (React.createElement(Box, null,
            React.createElement(Box, { sx: {
                    pl: 5,
                    pt: 2,
                    borderBottom: '1px',
                    borderColor: 'divider',
                    boxShadow: 3,
                } },
                React.createElement(Tabs, { variant: 'scrollable', value: activeTab, onChange: (event, value) => {
                        navigationContext.navigate(() => {
                            var _a;
                            const path = sections && sections[value] ? urlify(sections[value].columns.title) : 'history';
                            navigate({ search: createSearchParams({ tab: path }).toString() }, { replace: true, state: { back: (_a = location.state) === null || _a === void 0 ? void 0 : _a.back } });
                        });
                    } },
                    sections.map((section, index) => {
                        let isSectionComplete = true;
                        if (validationResult.incompleteSections.length !== 0) {
                            validationResult.incompleteSections.forEach((s) => {
                                if (s.id === section.id) {
                                    isSectionComplete = false;
                                }
                            });
                        }
                        return isSectionComplete ? (React.createElement(Tab, { key: index, value: index, label: section.columns.title, "data-test": `profile-${urlify(section.columns.title)}-tab` })) : (React.createElement(Tab, { key: index, value: index, label: section.columns.title, sx: { pr: 0.5 }, iconPosition: 'end', icon: React.createElement(ErrorOutlineOutlinedIcon, { key: index, sx: { color: theme.palette.error.main, width: '16px', height: '16px' } }), "data-test": `profile-${urlify(section.columns.title)}-tab` }));
                    }),
                    allowViewAccountHistory && (React.createElement(Tab, { key: sections.length + 1, value: sections.length + 1, label: t('panels.accounts.history'), "data-test": 'profile-history-tab' })))),
            activeTab < sections.length ? (React.createElement(Box, { sx: { py: 4, px: 5 }, key: activeTab },
                React.createElement(AccountSectionComponent, { userState: userState, key: account.id, section: sections[activeTab], showSubmitButton: true, page: 'profile', data: account.data, accountId: account.id, onSubmit: (data, auditTags) => __awaiter(void 0, void 0, void 0, function* () {
                        // TODO widget 'form.file' is not working with JWT token
                        const generateAttachmentUrl = (key) => __awaiter(void 0, void 0, void 0, function* () {
                            return getAttachmentUrl({ entity: accountEntityName, entityId: account.id || '', key });
                        });
                        const uploadedFileKeys = (yield accountAttachmentStorage.list({ entity: accountEntityName, entityId: account.id })).map((file) => file.key);
                        const { files: filesFromData, submitData } = yield collectFilesAndFilterData(data, generateAttachmentUrl);
                        const filesToUpload = filesFromData.filter((file) => !uploadedFileKeys.includes(file.entityKey));
                        return accountStorage
                            .post({
                            action: 'partial-update',
                            id: account.id,
                            data: submitData,
                            auditTags,
                        })
                            .then((response) => {
                            if (filesToUpload.length !== 0) {
                                showProgressDialog({
                                    title: t('common.uploadingFiles'),
                                    callback: (done) => __awaiter(void 0, void 0, void 0, function* () {
                                        for (const { entityKey, file } of filesToUpload) {
                                            yield accountAttachmentStorage.post({
                                                entity: accountEntityName,
                                                entityId: response.id,
                                                key: entityKey,
                                                contentType: file.type ? file.type : 'application/octet-stream',
                                                contentLength: file.size,
                                                content: file,
                                            });
                                        }
                                        done();
                                    }),
                                });
                            }
                            showInfoSnackbar({ message: t('common.saved'), autoHideDuration: 2000 });
                            // invalidate state only for current user
                            if ((userState === null || userState === void 0 ? void 0 : userState.id) === account.id) {
                                userClient
                                    .invalidateUserState()
                                    .then(() => onStatelessReload())
                                    .catch(handleErrorSnackbar);
                            }
                        })
                            .catch(handleErrorDialog);
                    }), onSubmitSuccess: () => setReload({}) }))) : (allowViewAccountHistory &&
                account && (React.createElement(Box, { sx: { px: 5, py: 2 } },
                React.createElement(EntityAudit, { entitiesList: [
                        { entity: accountEntityName, entityIds: [account.id] },
                        {
                            entity: groupAccountMapEntityName,
                            entityIds: [],
                        },
                    ], includeRelatedEntities: true, customAuditBackendDataProvider: useAccountAuditBackendDataProvider, formatters: accountEntityAuditFormatters, auditTags: sections.map((section) => ({
                        value: mapAccountSectionToAuditTag(section.columns),
                        label: section.columns.title,
                    })) }))))))));
};
const allowedSections = (userState, account, sections) => {
    if (adminOrSelf(userState, account === null || account === void 0 ? void 0 : account.id) || allowOrgMembersEdit(userState)) {
        if (allowAccessToGroups(userState)) {
            return sections;
        }
        else {
            return sections === null || sections === void 0 ? void 0 : sections.filter((section) => section.columns.key !== 'groups');
        }
    }
    const orgRolesAllowedToSeeOnlyMembershipSection = ['viewAndTransferMembers'];
    const filterOnlyMembershipsSection = (account === null || account === void 0 ? void 0 : account.data.orgRole) &&
        Object.keys(account.data.orgRole).some((orgId) => (userState === null || userState === void 0 ? void 0 : userState.orgRole[orgId]) && orgRolesAllowedToSeeOnlyMembershipSection.includes(userState === null || userState === void 0 ? void 0 : userState.orgRole[orgId]));
    if (filterOnlyMembershipsSection) {
        return sections === null || sections === void 0 ? void 0 : sections.filter((section) => section.columns.key === 'membership');
    }
    return [];
};
function urlify(url) {
    return url.trim().toLowerCase().replace(/\s/g, '');
}
function getAttachmentUrl(param) {
    const baseUrl = baseClientDefaults.baseUrl;
    const entityParam = encodeURIComponent(param.entity);
    const entityIdParam = encodeURIComponent(param.entityId);
    const keyParam = encodeURIComponent(param.key);
    return `${baseUrl}/api/v1/attachment/get?entity=${entityParam}&entityId=${entityIdParam}&key=${keyParam}`;
}
